import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Clock } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import Logo from '../Logo';

const Footer = () => {
  const { businessInfo } = useSettings();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[var(--bg-secondary)]">
      <div className="container section-padding-small">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" aria-label="HomeGlow Nails Home">
              <Logo size="default" />
            </Link>
            <p className="mt-4 text-[var(--text-secondary)] body-small max-w-xs">
              {businessInfo.tagline}
            </p>
            <a 
              href={businessInfo.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-[var(--bg-card)] rounded-full text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors"
            >
              <Instagram size={16} />
              <span className="body-small">{businessInfo.instagram}</span>
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-medium text-[var(--text-heading)] mb-5" style={{fontFamily: "'Cormorant Garamond', serif", fontSize: '1.125rem'}}>
              Quick Links
            </h4>
            <nav className="flex flex-col gap-3">
              <Link to="/services" className="body-small text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors">
                Services & Pricing
              </Link>
              <Link to="/gallery" className="body-small text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors">
                Gallery
              </Link>
              <Link to="/book" className="body-small text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors">
                Book Appointment
              </Link>
              <Link to="/about" className="body-small text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors">
                About
              </Link>
              <Link to="/contact" className="body-small text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors">
                Contact
              </Link>
            </nav>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-sm font-medium text-[var(--text-heading)] mb-5" style={{fontFamily: "'Cormorant Garamond', serif", fontSize: '1.125rem'}}>
              Studio Hours
            </h4>
            <div className="space-y-3">
              <div>
                <p className="body-small text-[var(--text-heading)] font-medium">Mon - Fri</p>
                <p className="body-small text-[var(--text-secondary)]">{businessInfo.hours.weekdays}</p>
              </div>
              <div>
                <p className="body-small text-[var(--text-heading)] font-medium">Saturday</p>
                <p className="body-small text-[var(--text-secondary)]">{businessInfo.hours.saturday}</p>
              </div>
              <div>
                <p className="body-small text-[var(--text-heading)] font-medium">Sunday</p>
                <p className="body-small text-[var(--text-secondary)]">{businessInfo.hours.sunday}</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-medium text-[var(--text-heading)] mb-5" style={{fontFamily: "'Cormorant Garamond', serif", fontSize: '1.125rem'}}>
              Get in Touch
            </h4>
            <div className="space-y-3">
              <a 
                href={`tel:${businessInfo.phone}`}
                className="flex items-center gap-3 body-small text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-[var(--bg-card)] rounded-full">
                  <Phone size={14} />
                </div>
                {businessInfo.phone}
              </a>
              <a 
                href={`mailto:${businessInfo.email}`}
                className="flex items-center gap-3 body-small text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-[var(--bg-card)] rounded-full">
                  <Mail size={14} />
                </div>
                <span className="truncate">{businessInfo.email}</span>
              </a>
              <div className="flex items-center gap-3 body-small text-[var(--text-secondary)]">
                <div className="w-8 h-8 flex items-center justify-center bg-[var(--bg-card)] rounded-full">
                  <MapPin size={14} />
                </div>
                <span>{businessInfo.location}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-6 border-t border-[var(--border-light)]">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-center sm:text-left">
            <p className="body-small text-[var(--text-light)]">
              © {currentYear} {businessInfo.name}. All rights reserved.
            </p>
            <p className="body-small text-[var(--text-light)]">
              Serving Kitchener, Waterloo & Cambridge
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
